import React from 'react';
import { 
  FiUsers, 
  FiDollarSign, 
  FiActivity,
  FiArrowUp,
  FiArrowDown,
  FiMinus
} from 'react-icons/fi';
import MonthlyMemberAcquisitionChart from './MonthlyMemberAcquisitionChart';
import RevenuePerMemberTrendChart from './RevenuePerMemberTrendChart';
import AvgServicesPerMemberChart from './AvgServicesPerMemberChart';
import MemberSegmentsByTenureChart from './MemberSegmentsByTenureChart';

interface MetricCardProps {
  title: string;
  value: string | number;
  change: string;
  target: string;
  icon: React.ReactNode;
  isPositive: boolean | null; // null for neutral (0%)
}

const MetricCard: React.FC<MetricCardProps> = ({ 
  title, 
  value, 
  change, 
  target, 
  icon, 
  isPositive 
}) => {
  return (
    <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
      <div className="flex items-start justify-between">
        <div className="flex-grow">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-slate-600 font-medium text-xs sm:text-sm">{title}</h3>
            <div className="bg-slate-100 p-1.5 sm:p-2 rounded-full">
              {icon}
            </div>
          </div>
          
          <div className="mb-3">
            <p className="text-2xl sm:text-3xl font-bold text-slate-800 mb-1">{value}</p>
            <div className="flex items-center space-x-2">
              <span className={`flex items-center text-xs sm:text-sm font-medium ${
                isPositive === null ? 'text-slate-500' : isPositive ? 'text-green-600' : 'text-red-600'
              }`}>
                {isPositive === null ? (
                  <FiMinus className="mr-1" size={12} />
                ) : isPositive ? (
                  <FiArrowUp className="mr-1" size={12} />
                ) : (
                  <FiArrowDown className="mr-1" size={12} />
                )}
                {change}
              </span>
            </div>
          </div>
          
          <div className="pt-3 border-t border-slate-200">
            <p className="text-slate-500 text-xs sm:text-sm">
              Target: <span className="font-medium text-slate-700">{target}</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

interface ProgressBarProps {
  title: string;
  current: number;
  target: number;
  format: 'number' | 'currency' | 'percentage';
}

const ProgressBar: React.FC<ProgressBarProps> = ({ title, current, target, format }) => {
  const percentage = Math.min((current / target) * 100, 100);
  
  const formatValue = (value: number) => {
    switch (format) {
      case 'currency':
        return `$${value.toLocaleString()}`;
      case 'percentage':
        return `${value}%`;
      default:
        return value.toLocaleString();
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <h5 className="text-slate-700 font-medium">{title}</h5>
        <span className="text-slate-600 text-sm">
          {formatValue(current)} / {formatValue(target)}
        </span>
      </div>
      <div className="w-full bg-slate-200 rounded-full h-3">
        <div 
          className="bg-slate-800 h-3 rounded-full transition-all duration-300"
          style={{ width: `${percentage}%` }}
        ></div>
      </div>
    </div>
  );
};

const MemberAcquisition: React.FC = () => {
  const acquisitionMetrics = [
    {
      title: 'New Members (This Month)',
      value: '260',
      change: '-3.7%',
      target: '300',
      icon: <FiUsers className="text-slate-600" size={20} />,
      isPositive: false
    },
    {
      title: 'Total Member Base',
      value: '13,680',
      change: '+8.5%',
      target: '15,000',
      icon: <FiUsers className="text-slate-600" size={20} />,
      isPositive: true
    },
    {
      title: 'Revenue per Member',
      value: '$606',
      change: '0%',
      target: '$650',
      icon: <FiDollarSign className="text-slate-600" size={20} />,
      isPositive: null
    },
    {
      title: 'Avg Services per Member',
      value: '2.8',
      change: '+16.7%',
      target: '3.2',
      icon: <FiActivity className="text-slate-600" size={20} />,
      isPositive: true
    }
  ];

  return (
    <div className="space-y-6">
      {/* Key Metrics Grid */}
      <div>
        <h3 className="text-base sm:text-lg font-medium text-slate-700 mb-4">Member acquisition and growth metrics</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {acquisitionMetrics.map((metric, index) => (
            <MetricCard key={index} {...metric} />
          ))}
        </div>
      </div>

      {/* Monthly Acquisition & Revenue Trend Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
          <h4 className="text-base sm:text-lg font-medium text-slate-700 mb-4">Monthly New Member Acquisition</h4>
          <div className="h-64 sm:h-80">
            <MonthlyMemberAcquisitionChart />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
          <h4 className="text-base sm:text-lg font-medium text-slate-700 mb-4">Revenue per Member Trend</h4>
          <div className="h-64 sm:h-80">
            <RevenuePerMemberTrendChart />
          </div>
        </div>
      </div>

      {/* Services & Segments Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
          <h4 className="text-base sm:text-lg font-medium text-slate-700 mb-4">Average Services per Member</h4>
          <div className="h-64 sm:h-80">
            <AvgServicesPerMemberChart />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
          <h4 className="text-base sm:text-lg font-medium text-slate-700 mb-4">Members by Length of Membership</h4>
          <div className="h-96 sm:h-80">
            <MemberSegmentsByTenureChart />
          </div>
        </div>
      </div>

      {/* Progress Towards Annual Goals */}
      <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
        <h4 className="text-base sm:text-lg font-medium text-slate-700 mb-6">Progress Towards Annual Goals</h4>
        <div className="space-y-6">
          <ProgressBar
            title="Member Base Growth"
            current={8.5}
            target={15}
            format="percentage"
          />
          <ProgressBar
            title="Revenue per Member"
            current={606}
            target={650}
            format="currency"
          />
          <ProgressBar
            title="Total Members"
            current={13680}
            target={15000}
            format="number"
          />
        </div>
      </div>
    </div>
  );
};

export default MemberAcquisition;